import { useState, useEffect, useCallback } from 'react';
import { api, type AdminAnalyticsEngagement, type AnalyticsPeriod } from '@/lib/api';
import { PeriodSelector } from './components/PeriodSelector';
import { StackedAreaChart } from './components/StackedAreaChart';
import { BreakdownTable } from './components/BreakdownTable';

export function EngagementTab() {
  const [period, setPeriod] = useState<AnalyticsPeriod>('30d');
  const [data, setData] = useState<AdminAnalyticsEngagement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(
    async (nocache = false) => {
      setLoading(true);
      setError(null);
      try {
        const result = await api.adminGetAnalyticsEngagement(period, nocache);
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load engagement');
      } finally {
        setLoading(false);
      }
    },
    [period],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <PeriodSelector
          period={period}
          onChange={setPeriod}
          onRefresh={() => fetchData(true)}
          loading={loading}
        />
      </div>

      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          {error}
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-card rounded-lg border border-border p-6">
              <h2 className="text-base font-semibold text-foreground mb-3">By Unique Users</h2>
              <BreakdownTable
                rows={data.topArchesByUniqueUsers}
                columns={[
                  { key: 'archeId', label: 'Arche' },
                  { key: 'uniqueUsers', label: 'Users', align: 'right' },
                ]}
              />
            </div>
            <div className="bg-card rounded-lg border border-border p-6">
              <h2 className="text-base font-semibold text-foreground mb-1">Est. Active Time</h2>
              <p className="text-xs text-muted-foreground mb-3">
                Sum of 30s heartbeats while page was visible &amp; user was active. Under-counts
                browser-throttled / mobile-backgrounded sessions.
              </p>
              <BreakdownTable
                rows={data.topArchesByEstimatedActiveTime}
                columns={[
                  { key: 'archeId', label: 'Arche' },
                  {
                    key: 'estimatedActiveMinutes',
                    label: 'Est. min',
                    align: 'right',
                    render: (r) => r.estimatedActiveMinutes.toLocaleString(),
                  },
                ]}
              />
            </div>
            <div className="bg-card rounded-lg border border-border p-6">
              <h2 className="text-base font-semibold text-foreground mb-3">By Credits</h2>
              <BreakdownTable
                rows={data.topArchesByCredits}
                columns={[
                  { key: 'archeId', label: 'Arche' },
                  {
                    key: 'totalCredits',
                    label: 'Spend',
                    align: 'right',
                    render: (r) => `$${(r.totalCredits / 100).toFixed(2)}`,
                  },
                ]}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-card rounded-lg border border-border p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Feature Adoption</h2>
              <BreakdownTable
                rows={data.featureAdoption}
                columns={[
                  { key: 'feature', label: 'Feature' },
                  { key: 'uniqueUsers', label: 'Users', align: 'right' },
                  { key: 'totalUses', label: 'Uses', align: 'right' },
                ]}
              />
            </div>
            <div className="bg-card rounded-lg border border-border p-6">
              <h2 className="text-lg font-semibold text-foreground mb-1">Workflow Funnel</h2>
              <p className="text-xs text-muted-foreground mb-3">
                Client-reported funnel — see Payments tool for authoritative purchase counts.
              </p>
              <BreakdownTable
                rows={data.workflowFunnel}
                columns={[
                  { key: 'workflow', label: 'Workflow' },
                  { key: 'started', label: 'Started', align: 'right' },
                  { key: 'completed', label: 'Done', align: 'right' },
                  { key: 'abandoned', label: 'Abandoned', align: 'right' },
                  {
                    key: 'completionRate',
                    label: 'Rate',
                    align: 'right',
                    render: (r) => `${(r.completionRate * 100).toFixed(0)}%`,
                  },
                ]}
              />
            </div>
          </div>

          <div className="bg-card rounded-lg border border-border p-6 mb-6">
            <h2 className="text-lg font-semibold text-foreground mb-2">Daily Spend by Bucket</h2>
            <p className="text-xs text-muted-foreground mb-4">
              Three-bucket credit system: subscription (paid for via plan), fias (purchased
              outright), system (platform allowance).
            </p>
            <StackedAreaChart
              data={data.bucketSpendDaily}
              series={[
                { dataKey: 'subscription', label: 'Subscription', color: '#3b82f6' },
                { dataKey: 'fias', label: 'Fias (purchased)', color: '#22c55e' },
                { dataKey: 'system', label: 'System (platform)', color: '#f59e0b' },
              ]}
            />
          </div>

          <div className="bg-card rounded-lg border border-border p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Expense by Category</h2>
            <BreakdownTable
              rows={data.expenseByCategory}
              columns={[
                { key: 'category', label: 'Category' },
                { key: 'count', label: 'Count', align: 'right' },
                {
                  key: 'totalCredits',
                  label: 'Spend',
                  align: 'right',
                  render: (r) => `$${(r.totalCredits / 100).toFixed(2)}`,
                },
              ]}
            />
          </div>
        </>
      )}
    </div>
  );
}
